import React, { useState } from "react";
import { motion } from "framer-motion";
import { showToast } from "./Toast";

/**
 * BookingLinkSelector - Component to show and copy booking links
 *
 * Allows users to choose between traditional calendar booking
 * or AI-powered conversational booking.
 */
const BookingLinkSelector = ({ bookingPageSlug }) => {
  const [selectedMode, setSelectedMode] = useState("traditional");

  const baseUrl = window.location.origin;
  const traditionalLink = `${baseUrl}/book/${bookingPageSlug}`;
  const aiLink = `${baseUrl}/ai-book/${bookingPageSlug}`;

  const currentLink = selectedMode === "traditional" ? traditionalLink : aiLink;

  const copyToClipboard = (link) => {
    navigator.clipboard.writeText(link);
    showToast.success("Link copied to clipboard!");
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
      <h3 className="text-lg font-bold text-gray-900 mb-4">Share Your Booking Link</h3>

      {/* Mode Selector */}
      <div className="flex gap-2 mb-4 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setSelectedMode("traditional")}
          className={`flex-1 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
            selectedMode === "traditional"
              ? "bg-white text-indigo-600 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          📅 Calendar View
        </button>
        <button
          onClick={() => setSelectedMode("ai")}
          className={`flex-1 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
            selectedMode === "ai"
              ? "bg-white text-indigo-600 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          🤖 AI Chat
        </button>
      </div>

      {/* Link Display */}
      <div className="mb-4">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <code className="flex-1 text-sm text-gray-700 truncate">{currentLink}</code>
          <button
            onClick={() => copyToClipboard(currentLink)}
            className="px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-semibold whitespace-nowrap"
          >
            📋 Copy
          </button>
        </div>
      </div>

      {/* Mode Descriptions */}
      <motion.div
        key={selectedMode}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3"
      >
        {selectedMode === "traditional" ? (
          <div>
            <p className="font-semibold text-blue-900 mb-1">📅 Traditional Calendar Booking</p>
            <p>Guests select date/time from a visual calendar interface. Best for users who prefer clicking and selecting from available slots.</p>
          </div>
        ) : (
          <div>
            <p className="font-semibold text-purple-900 mb-1">🤖 AI-Powered Chat Booking</p>
            <p>Guests chat with AI agents (Max & Lucy) to book appointments conversationally. Best for a personalized, guided experience.</p>
          </div>
        )}
      </motion.div>

      {/* Quick Actions */}
      <div className="mt-4 flex gap-2">
        <button
          onClick={() => {
            copyToClipboard(traditionalLink);
          }}
          className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-medium"
        >
          Copy Calendar Link
        </button>
        <button
          onClick={() => {
            copyToClipboard(aiLink);
          }}
          className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-xs font-medium"
        >
          Copy AI Chat Link
        </button>
      </div>
    </div>
  );
};

export default BookingLinkSelector;
